import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  crmApi,
  customerOrderFocusPath,
  extractCrmError,
  getCustomerOrderSourceLabel,
  getCustomerOrderStatusLabel,
  CUSTOMER_ORDER_SOURCE_OPTIONS,
  CUSTOMER_ORDER_STATUS_OPTIONS,
} from "@/lib/crmApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, CarFront, Loader2, Plus, Search, Send, Trash2, UserRound } from "lucide-react";
import ClientSearch, {
  formatPhone,
  formatVehicle,
  normalizeClientName,
} from "@/components/customer-orders/ClientSearch";
import { calculateDraftTotals, calculateLineTotal } from "@/lib/customerOrderDraft";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function parseDateInput(value) {
  if (!value) return "";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleDateString("en-CA");
}

function addDays(base, days) {
  const next = new Date(base);
  next.setDate(next.getDate() + days);
  return next;
}

function isWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

export function getDefaultPromisedDeliveryDate(reference = new Date()) {
  const now = new Date(reference);
  const afterCutoff = now.getHours() >= 18;
  let candidate = addDays(now, afterCutoff ? 2 : 1);
  while (isWeekend(candidate)) {
    candidate = addDays(candidate, 1);
  }
  return parseDateInput(candidate);
}

function createItemDraft(index = 0) {
  return {
    __key: `item-${Date.now()}-${index}-${Math.random().toString(16).slice(2)}`,
    id: null,
    product_id: "",
    product_code: "",
    product_name: "",
    quantity: "1",
    price: "0",
    discount_percent: "0",
    supplier_price_snapshot: "",
    supplier_id_snapshot: "",
    comment: "",
  };
}

function createEmptyDraft({
  client_id = null,
  source = "manual",
  source_message_id = null,
  source_chat_id = null,
} = {}) {
  return {
    client_id,
    source,
    source_message_id,
    source_chat_id,
    status: "draft",
    promised_delivery_date: getDefaultPromisedDeliveryDate(),
    manager_comment: "",
    customer_comment: "",
    discount_percent: "0",
    items: [createItemDraft(0)],
  };
}

function orderToDraft(order) {
  const items = (order.items || []).map((item, index) => ({
    __key: `item-${item.id}`,
    id: item.id,
    product_id: item.product_id || "",
    product_code: item.product_code || "",
    product_name: item.product_name || "",
    quantity: String(item.quantity ?? 1),
    price: String(item.price ?? 0),
    discount_percent: String(item.discount_percent ?? 0),
    supplier_price_snapshot:
      item.supplier_price_snapshot != null ? String(item.supplier_price_snapshot) : "",
    supplier_id_snapshot: item.supplier_id_snapshot || "",
    comment: item.comment || "",
  }));
  return {
    client_id: order.client_id,
    source: order.source || "manual",
    source_message_id: order.source_message_id || null,
    source_chat_id: order.source_chat_id || null,
    status: order.status || "draft",
    promised_delivery_date: parseDateInput(order.promised_delivery_date),
    manager_comment: order.manager_comment || "",
    customer_comment: order.customer_comment || "",
    discount_percent: String(order.discount_percent ?? 0),
    items: items.length ? items : [],
  };
}

function formatMoney(value, currency = "KZT") {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return `${new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n)} ${currency}`;
}

function validateDraft(draft, mode) {
  const fieldErrors = {};
  const itemErrors = {};

  if (mode === "new" && !draft.client_id) {
    fieldErrors.client = "Выберите клиента";
  }
  if (!draft.source) {
    fieldErrors.source = "Укажите источник";
  }
  if (!draft.status) {
    fieldErrors.status = "Укажите статус";
  }
  if (!draft.promised_delivery_date) {
    fieldErrors.promised_delivery_date = "Укажите дату поставки";
  }

  const cleanItems = [];
  draft.items.forEach((item) => {
    const rowErrors = {};
    const productName = String(item.product_name || "").trim();
    const productCode = String(item.product_code || "").trim();
    const productId = String(item.product_id || "").trim();
    const quantity = Number(item.quantity);
    const price = Number(item.price);
    const discountPercent = Number(item.discount_percent);

    if (!productName) rowErrors.product_name = "Укажите наименование";
    if (!Number.isFinite(quantity) || quantity <= 0) rowErrors.quantity = "Количество должно быть больше 0";
    if (!Number.isFinite(price) || price < 0) rowErrors.price = "Цена не может быть отрицательной";
    if (!Number.isFinite(discountPercent) || discountPercent < 0 || discountPercent > 100) {
      rowErrors.discount_percent = "Скидка должна быть в диапазоне 0-100";
    }

    if (Object.keys(rowErrors).length) {
      itemErrors[item.__key] = rowErrors;
    }

    cleanItems.push({
      id: item.id || null,
      product_id: productId || null,
      product_code: productCode || null,
      product_name: productName,
      quantity: quantity,
      price: price,
      discount_percent: discountPercent,
      supplier_price_snapshot:
        item.supplier_price_snapshot !== "" && item.supplier_price_snapshot != null
          ? Number(item.supplier_price_snapshot)
          : null,
      supplier_id_snapshot: String(item.supplier_id_snapshot || "").trim() || null,
      comment: String(item.comment || "").trim() || null,
    });
  });

  return { fieldErrors, itemErrors, cleanItems };
}

function formatProductLabel(product) {
  const name = (product.name || "").trim() || "—";
  const code = product.oem || product.articul || product.code || "";
  return code ? `${code} · ${name}` : name;
}

function ProductSearchField({ disabled, onSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await crmApi.listProducts({
          page: 1,
          page_size: 8,
          search: term,
          sort_by: "name",
          sort_order: "asc",
        });
        setResults(Array.isArray(data?.products) ? data.products : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (product) => {
    onSelect({
      product_id: product.id,
      product_code: product.oem || product.articul || product.code || "",
      product_name: (product.name || "").trim(),
      price: String(product.client_price ?? 0),
      supplier_price_snapshot:
        product.supplier_price != null ? String(product.supplier_price) : "",
      supplier_id_snapshot: product.supplier_id || "",
    });
    setQuery("");
    setResults([]);
  };

  return (
    <div className="relative">
      <Label>Поиск из прайса</Label>
      <div className="relative mt-2">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
          placeholder="Артикул, OEM или название"
          className="pl-9"
        />
      </div>
      {loading && <p className="mt-1 text-xs text-slate-500">Ищем товары...</p>}
      {results.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
          {results.map((product) => (
            <li key={product.id}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                onClick={() => handleSelect(product)}
              >
                <span className="font-medium text-slate-950">{formatProductLabel(product)}</span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  {formatMoney(product.client_price)} · {product.supplier_name || "—"}
                  {product.in_stock > 0 ? ` · в наличии ${product.in_stock}` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OrderItemsTable({
  items,
  itemErrors,
  currency,
  disabled,
  onAddItem,
  onAddFromProduct,
  onPatchItem,
  onRemoveItem,
}) {
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0 flex-1">
          <ProductSearchField disabled={disabled} onSelect={onAddFromProduct} />
        </div>
        <Button type="button" onClick={onAddItem} disabled={disabled} className="shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          Добавить позицию
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
          Пока нет позиций. Добавьте первую строку заказа или выберите товар из прайса.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/90 hover:bg-slate-50/90">
                <TableHead className="min-w-[110px] font-semibold text-slate-600">Артикул</TableHead>
                <TableHead className="min-w-[180px] font-semibold text-slate-600">Наименование</TableHead>
                <TableHead className="w-[88px] text-center font-semibold text-slate-600">Кол-во</TableHead>
                <TableHead className="w-[120px] text-right font-semibold text-slate-600">Цена</TableHead>
                <TableHead className="w-[96px] text-center font-semibold text-slate-600">Скидка %</TableHead>
                <TableHead className="min-w-[140px] font-semibold text-slate-600">Комментарий</TableHead>
                <TableHead className="w-[130px] text-right font-semibold text-slate-600">Сумма</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => {
                const error = itemErrors[item.__key];
                const rowTotal = calculateLineTotal(item);
                return (
                  <TableRow key={item.__key} className="align-top hover:bg-slate-50/70">
                    <TableCell className="py-2">
                      <Input
                        value={item.product_code}
                        onChange={(e) => onPatchItem(item.__key, { product_code: e.target.value })}
                        disabled={disabled}
                        placeholder="Артикул"
                        className="h-8 font-mono text-xs"
                      />
                    </TableCell>
                    <TableCell className="py-2">
                      <Input
                        value={item.product_name}
                        onChange={(e) => onPatchItem(item.__key, { product_name: e.target.value })}
                        disabled={disabled}
                        placeholder="Наименование"
                        className="h-8 text-sm"
                        aria-invalid={!!error?.product_name}
                      />
                      {error?.product_name && (
                        <p className="mt-1 text-xs text-red-600">{error.product_name}</p>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={item.quantity}
                        onChange={(e) => onPatchItem(item.__key, { quantity: e.target.value })}
                        disabled={disabled}
                        className="mx-auto h-8 w-16 text-center text-sm"
                        aria-invalid={!!error?.quantity}
                      />
                      {error?.quantity && <p className="mt-1 text-center text-xs text-red-600">{error.quantity}</p>}
                    </TableCell>
                    <TableCell className="py-2">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.price}
                        onChange={(e) => onPatchItem(item.__key, { price: e.target.value })}
                        disabled={disabled}
                        className="ml-auto h-8 w-24 text-right text-sm"
                        aria-invalid={!!error?.price}
                      />
                      {error?.price && <p className="mt-1 text-right text-xs text-red-600">{error.price}</p>}
                    </TableCell>
                    <TableCell className="py-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={item.discount_percent}
                        onChange={(e) => onPatchItem(item.__key, { discount_percent: e.target.value })}
                        disabled={disabled}
                        className="mx-auto h-8 w-16 text-center text-sm"
                        aria-invalid={!!error?.discount_percent}
                      />
                      {error?.discount_percent && (
                        <p className="mt-1 text-center text-xs text-red-600">{error.discount_percent}</p>
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      <Input
                        value={item.comment}
                        onChange={(e) => onPatchItem(item.__key, { comment: e.target.value })}
                        disabled={disabled}
                        placeholder="Комментарий"
                        className="h-8 min-w-[120px] text-sm"
                      />
                    </TableCell>
                    <TableCell className="py-2 text-right font-semibold text-slate-950">
                      {formatMoney(rowTotal, currency)}
                    </TableCell>
                    <TableCell className="py-2 text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemoveItem(item.__key)}
                        disabled={disabled}
                        className="h-8 w-8 p-0 hover:bg-red-50"
                        title="Удалить позицию"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function SelectedClientCard({ client, canEditName = false, clientName = "", onClientNameChange, disabled = false }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Клиент</p>
      {canEditName ? (
        <div className="mt-2">
          <Label htmlFor="client-name">Имя клиента</Label>
          <Input
            id="client-name"
            value={clientName}
            onChange={(e) => onClientNameChange(e.target.value)}
            disabled={disabled}
            placeholder="Введите имя клиента"
            className="mt-2"
          />
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-2">
          <UserRound className="h-4 w-4 text-slate-500" />
          <p className="text-base font-semibold text-slate-950">{normalizeClientName(client)}</p>
        </div>
      )}
      <p className="mt-1 text-sm text-slate-600">{formatPhone(client.phone || client.phone_normalized)}</p>
      <p className="mt-1 text-sm text-slate-600">
        <CarFront className="mr-1 inline-block h-4 w-4 text-slate-400" />
        {formatVehicle(client.primary_vehicle || client.vehicles?.[0] || client.detected_car_model)}
      </p>
      <p className="mt-3 text-xs text-slate-500">
        {canEditName
          ? "Можно изменить только имя клиента. Для смены самого клиента создайте новый заказ."
          : "Клиент меняется только при создании заказа. Для изменения клиента создайте новый заказ."}
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-8 w-64 rounded-full" />
      <Skeleton className="h-24 w-full rounded-2xl" />
      <div className="grid gap-3 lg:grid-cols-2">
        <Skeleton className="h-44 rounded-2xl" />
        <Skeleton className="h-44 rounded-2xl" />
      </div>
      <Skeleton className="h-56 w-full rounded-2xl" />
    </div>
  );
}

export default function CustomerOrderFormDialog({
  open,
  mode,
  orderId,
  onClose,
  onSaved,
  initialClientId = null,
  initialSource = "manual",
  initialSourceMessageId = null,
  initialSourceChatId = null,
}) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [form, setForm] = useState(createEmptyDraft());
  const [selectedClient, setSelectedClient] = useState(null);
  const [preloadingClient, setPreloadingClient] = useState(false);
  const [clientBootstrapError, setClientBootstrapError] = useState(null);
  const [clientNameDraft, setClientNameDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingToClient, setSendingToClient] = useState(false);
  const [itemErrors, setItemErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [currentOrder, setCurrentOrder] = useState(null);
  const initialOrderRef = useRef(null);

  const isEdit = mode === "edit";
  const isNew = mode === "new";
  const currency = currentOrder?.currency || "KZT";

  const preview = useMemo(
    () => calculateDraftTotals(form.items, Number(form.discount_percent) || 0),
    [form.items, form.discount_percent]
  );

  const close = () => {
    if (typeof onClose === "function") {
      onClose();
      return;
    }
    navigate("/crm/customer-orders");
  };

  const syncFromOrder = async (order) => {
    setCurrentOrder(order);
    setForm(orderToDraft(order));
    initialOrderRef.current = order;
    if (order?.client_id) {
      try {
        const client = await crmApi.getClient(token, order.client_id);
        setSelectedClient(client);
        setClientNameDraft(normalizeClientName(client));
      } catch (err) {
        // клиент может не подгрузиться, но заказ редактировать можно.
        setSelectedClient({
          id: order.client_id,
          display_name: order.client_name_snapshot || "Клиент",
          phone: order.phone_snapshot || null,
          primary_vehicle: null,
        });
        setClientNameDraft(order.client_name_snapshot || "");
      }
    } else {
      setSelectedClient(null);
      setClientNameDraft("");
    }
  };

  useEffect(() => {
    if (!open) return;
    setLoadError(null);
    setSaveError(null);
    setFieldErrors({});
    setItemErrors({});
    setSaving(false);
    setSendingToClient(false);
    if (isNew) {
      setLoadingOrder(false);
      setCurrentOrder(null);
      initialOrderRef.current = null;
      setClientBootstrapError(null);
      const parsedClientId = Number(initialClientId);
      const parsedMessageId = Number(initialSourceMessageId);
      const safeClientId = Number.isInteger(parsedClientId) && parsedClientId > 0 ? parsedClientId : null;
      const safeMessageId = Number.isInteger(parsedMessageId) && parsedMessageId > 0 ? parsedMessageId : null;
      const safeSource = CUSTOMER_ORDER_SOURCE_OPTIONS.some((item) => item.value === initialSource)
        ? initialSource
        : "manual";
      setForm(
        createEmptyDraft({
          client_id: safeClientId,
          source: safeSource,
          source_message_id: safeMessageId,
          source_chat_id: initialSourceChatId || null,
        })
      );
      setSelectedClient(null);
      setClientNameDraft("");
      setPreloadingClient(false);
      if (!safeClientId) {
        return;
      }
      let cancelled = false;
      setPreloadingClient(true);
      (async () => {
        try {
          const client = await crmApi.getClient(token, safeClientId);
          if (cancelled) return;
          setSelectedClient(client);
          setClientNameDraft(normalizeClientName(client));
          setClientBootstrapError(null);
          setForm((prev) => ({
            ...prev,
            client_id: client.id,
            source_chat_id: prev.source_chat_id || client.whatsapp_chat_id || null,
          }));
        } catch (err) {
          if (!cancelled) {
            setClientBootstrapError(extractCrmError(err, "Не удалось загрузить клиента для заказа"));
            setForm((prev) => ({ ...prev, client_id: null }));
          }
        } finally {
          if (!cancelled) setPreloadingClient(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    if (isEdit && orderId) {
      let cancelled = false;
      setLoadingOrder(true);
      (async () => {
        try {
          const order = await crmApi.getCustomerOrder(token, orderId);
          if (cancelled) return;
          await syncFromOrder(order);
        } catch (err) {
          if (!cancelled) {
            setLoadError(extractCrmError(err, "Не удалось загрузить заказ"));
          }
        } finally {
          if (!cancelled) setLoadingOrder(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }
  }, [open, isNew, isEdit, orderId, token, initialClientId, initialSource, initialSourceMessageId, initialSourceChatId]);

  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const addItem = (productPatch = null) => {
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          ...createItemDraft(prev.items.length),
          ...(productPatch || {}),
        },
      ],
    }));
  };

  const addItemFromProduct = (productPatch) => {
    addItem(productPatch);
  };

  const removeItem = (key) => {
    const item = form.items.find((row) => row.__key === key);
    if (!item) return;
    if (!window.confirm("Удалить позицию?")) return;
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((row) => row.__key !== key),
    }));
  };

  const patchItem = (key, patch) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((row) => (row.__key === key ? { ...row, ...patch } : row)),
    }));
  };

  const persistOrder = async () => {
    const normalizedMode = isEdit ? "edit" : "new";
    const { fieldErrors: nextFieldErrors, itemErrors: nextItemErrors, cleanItems } = validateDraft(form, normalizedMode);
    setFieldErrors(nextFieldErrors);
    setItemErrors(nextItemErrors);
    setSaveError(null);

    if (Object.keys(nextFieldErrors).length || Object.keys(nextItemErrors).length) {
      return { ok: false };
    }

    setSaving(true);
    try {
      if (isNew) {
        const payload = {
          client_id: form.client_id,
          source: form.source,
          source_message_id: form.source_message_id || null,
          source_chat_id: form.source_chat_id || null,
          status: form.status,
          promised_delivery_date: form.promised_delivery_date || null,
          manager_comment: form.manager_comment || null,
          customer_comment: form.customer_comment || null,
          discount_percent: Number(form.discount_percent) > 0 ? Number(form.discount_percent) : null,
          items: cleanItems.map((item, index) => ({
            ...item,
            sort_order: index + 1,
          })),
        };
        const created = await crmApi.createCustomerOrder(token, payload);
        if (typeof onSaved === "function") {
          await onSaved(created);
        }
        return { ok: true, order: created, created: true };
      }

      const original = initialOrderRef.current;
      if (!original) {
        throw new Error("Заказ не загружен");
      }
      if (selectedClient?.id) {
        const nextClientName = String(clientNameDraft || "").trim();
        const prevClientName = normalizeClientName(selectedClient);
        if (nextClientName !== prevClientName) {
          const updatedClient = await crmApi.patchClient(token, selectedClient.id, {
            manual_name: nextClientName || null,
          });
          setSelectedClient(updatedClient);
          setClientNameDraft(normalizeClientName(updatedClient));
        }
      }

      const headerPatch = {};
      if (form.source !== original.source) headerPatch.source = form.source;
      if ((form.promised_delivery_date || null) !== (parseDateInput(original.promised_delivery_date) || null)) {
        headerPatch.promised_delivery_date = form.promised_delivery_date || null;
      }
      if ((form.manager_comment || "") !== (original.manager_comment || "")) {
        headerPatch.manager_comment = form.manager_comment || null;
      }
      if ((form.customer_comment || "") !== (original.customer_comment || "")) {
        headerPatch.customer_comment = form.customer_comment || null;
      }
      const nextOrderDiscount =
        form.discount_percent === "" || form.discount_percent == null
          ? null
          : Number(form.discount_percent);
      const prevOrderDiscount =
        original.discount_percent == null ? null : Number(original.discount_percent);
      if (nextOrderDiscount !== prevOrderDiscount) {
        headerPatch.discount_percent = nextOrderDiscount;
      }
      if (Object.keys(headerPatch).length) {
        await crmApi.patchCustomerOrder(token, original.id, headerPatch);
      }

      const originalStatus = original.status || "draft";
      if (form.status !== originalStatus) {
        await crmApi.changeCustomerOrderStatus(token, original.id, form.status);
      }

      const originalItemsById = new Map((original.items || []).map((item) => [item.id, item]));
      const currentItemsById = new Map(cleanItems.filter((item) => item.id).map((item) => [item.id, item]));

      for (const [index, item] of cleanItems.entries()) {
        const sortOrder = index + 1;
        if (!item.id) {
          await crmApi.addCustomerOrderItem(token, original.id, {
            ...item,
            sort_order: sortOrder,
          });
          continue;
        }

        const before = originalItemsById.get(item.id);
        if (!before) continue;
        const changed =
          before.product_id !== item.product_id ||
          before.product_code !== item.product_code ||
          before.product_name !== item.product_name ||
          Number(before.quantity) !== Number(item.quantity) ||
          Number(before.price) !== Number(item.price) ||
          Number(before.discount_percent || 0) !== Number(item.discount_percent) ||
          Number(before.supplier_price_snapshot || 0) !== Number(item.supplier_price_snapshot || 0) ||
          (before.supplier_id_snapshot || "") !== (item.supplier_id_snapshot || "") ||
          (before.comment || "") !== (item.comment || "") ||
          Number(before.sort_order || sortOrder) !== Number(sortOrder);

        if (changed) {
          await crmApi.patchCustomerOrderItem(token, original.id, item.id, {
            ...item,
            sort_order: sortOrder,
          });
        }
      }

      for (const before of original.items || []) {
        if (!currentItemsById.has(before.id)) {
          await crmApi.deleteCustomerOrderItem(token, original.id, before.id);
        }
      }

      const refreshed = await crmApi.getCustomerOrder(token, original.id);
      await syncFromOrder(refreshed);
      if (typeof onSaved === "function") {
        await onSaved(refreshed);
      }
      return { ok: true, order: refreshed };
    } catch (err) {
      setSaveError(extractCrmError(err, "Не удалось сохранить заказ"));
      toast.error(extractCrmError(err, "Не удалось сохранить заказ"));
      return { ok: false };
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving || sendingToClient) return;

    const result = await persistOrder();
    if (!result.ok) return;

    if (result.created) {
      toast.success("Заказ создан");
      navigate(customerOrderFocusPath(result.order.id), { replace: true });
      return;
    }

    toast.success("Заказ сохранён");
  };

  const handleSendToClient = async () => {
    if (!token || sendingToClient || saving) return;
    setSendingToClient(true);
    try {
      const saveResult = await persistOrder();
      if (!saveResult.ok || !saveResult.order?.id) return;

      const res = await crmApi.sendCustomerOrderToClient(token, saveResult.order.id);
      if (!res.success) {
        if (res.error === "send_disabled") {
          toast.error("Отправка WhatsApp отключена в настройках CRM");
        } else if (res.error === "missing_chat_id") {
          toast.error("У клиента не указан WhatsApp-чат");
        } else {
          toast.error(res.error || "Не удалось отправить сообщение");
        }
        return;
      }
      toast.success("Заказ сохранён и отправлен клиенту в WhatsApp");
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось отправить заказ клиенту"));
    } finally {
      setSendingToClient(false);
    }
  };

  const title = isNew ? "Новый заказ покупателя" : "Редактирование заказа";

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && close()}>
      <DialogContent className="max-w-[1200px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {isNew
              ? "Создайте заказ покупателя, добавьте позиции и сохраните черновик или подтверждённый заказ."
              : "Изменяйте шапку, позиции и статус без перехода в legacy-модуль поставок."}
          </DialogDescription>
        </DialogHeader>

        {loadingOrder ? (
          <LoadingState />
        ) : loadError ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <AlertTriangle className="mr-2 inline-block h-4 w-4" />
            {loadError}
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-6">
            {saveError && (
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <AlertTriangle className="mr-2 inline-block h-4 w-4" />
                {saveError}
              </div>
            )}
          <Card className="border-slate-200 bg-slate-50/60 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Клиент</CardTitle>
              <CardDescription>
                {isNew
                  ? "Найдите существующего клиента по имени или телефону."
                  : "Клиент фиксируется при создании заказа. На этапе редактирования можно изменить только имя клиента."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {preloadingClient && isNew && (
                <div className="mb-3 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                  Подставляем клиента...
                </div>
              )}
              {clientBootstrapError && isNew && (
                <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {clientBootstrapError}
                </div>
              )}
              {isNew ? (
                <ClientSearch
                  token={token}
                  selectedClient={selectedClient}
                  disabled={saving || preloadingClient}
                  onSelect={(client) => {
                    setSelectedClient(client);
                    setClientNameDraft(client ? normalizeClientName(client) : "");
                    setClientBootstrapError(null);
                    updateForm({
                      client_id: client?.id || null,
                      source_chat_id: client?.whatsapp_chat_id || form.source_chat_id || null,
                    });
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.client;
                      return next;
                    });
                    }}
                  />
                ) : selectedClient ? (
                  <SelectedClientCard
                    client={selectedClient}
                    canEditName
                    clientName={clientNameDraft}
                    onClientNameChange={setClientNameDraft}
                    disabled={saving || sendingToClient}
                  />
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-4 text-sm text-slate-500">
                    Клиент не загружен.
                  </div>
                )}
                {fieldErrors.client && <p className="mt-2 text-sm text-red-600">{fieldErrors.client}</p>}
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader className="border-b border-slate-100 bg-slate-50/60">
                <CardTitle className="text-lg">Шапка заказа</CardTitle>
                <CardDescription>
                  Источник, дата поставки, комментарии и текущий статус.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 p-4 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <Label>Источник</Label>
                  <Select
                    value={form.source}
                    onValueChange={(value) => updateForm({ source: value })}
                    disabled={saving}
                  >
                    <SelectTrigger className="mt-2 h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CUSTOMER_ORDER_SOURCE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldErrors.source && <p className="mt-1 text-xs text-red-600">{fieldErrors.source}</p>}
                </div>

                <div>
                  <Label>Дата поставки</Label>
                  <Input
                    type="date"
                    value={form.promised_delivery_date}
                    onChange={(e) => updateForm({ promised_delivery_date: e.target.value })}
                    disabled={saving}
                    className="mt-2 h-11"
                  />
                  {fieldErrors.promised_delivery_date && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.promised_delivery_date}</p>
                  )}
                </div>

                <div>
                  <Label>Статус</Label>
                  <Select
                    value={form.status}
                    onValueChange={(value) => updateForm({ status: value })}
                    disabled={saving}
                  >
                    <SelectTrigger className="mt-2 h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CUSTOMER_ORDER_STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldErrors.status && <p className="mt-1 text-xs text-red-600">{fieldErrors.status}</p>}
                </div>

                <div className="flex items-end">
                  <Badge variant="outline" className="h-11 rounded-full px-4">
                    {getCustomerOrderStatusLabel(form.status)}
                  </Badge>
                </div>

                <div className="md:col-span-2">
                  <Label>Комментарий менеджера</Label>
                  <Textarea
                    rows={3}
                    value={form.manager_comment}
                    onChange={(e) => updateForm({ manager_comment: e.target.value })}
                    disabled={saving}
                    className="mt-2"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Комментарий для клиента</Label>
                  <Textarea
                    rows={3}
                    value={form.customer_comment}
                    onChange={(e) => updateForm({ customer_comment: e.target.value })}
                    disabled={saving}
                    className="mt-2"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader className="border-b border-slate-100 bg-slate-50/60">
                <CardTitle className="text-lg">Позиции заказа</CardTitle>
                <CardDescription>
                  Таблица позиций с поиском из прайса, редактированием цены, количества и скидки.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-4">
                <OrderItemsTable
                  items={form.items}
                  itemErrors={itemErrors}
                  currency={currency}
                  disabled={saving}
                  onAddItem={() => addItem()}
                  onAddFromProduct={addItemFromProduct}
                  onPatchItem={patchItem}
                  onRemoveItem={removeItem}
                />
              </CardContent>
            </Card>

            <div className="grid gap-4 xl:grid-cols-[1.6fr_1fr]">
              <Card className="border-slate-200 bg-white shadow-sm">
                <CardHeader className="border-b border-slate-100 bg-slate-50/60">
                  <CardTitle className="text-lg">Итоги</CardTitle>
                  <CardDescription>Учитываются скидки по позициям и общая скидка на заказ.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 p-4">
                  <div className="grid gap-3 md:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Сумма без скидки</p>
                      <p className="mt-2 text-2xl font-semibold text-slate-950">{formatMoney(preview.subtotal, currency)}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Скидка</p>
                      <p className="mt-2 text-2xl font-semibold text-slate-950">{formatMoney(preview.discount, currency)}</p>
                      {preview.lineDiscount > 0 && (
                        <p className="mt-1 text-xs text-slate-500">
                          по позициям: {formatMoney(preview.lineDiscount, currency)}
                        </p>
                      )}
                      {preview.orderDiscount > 0 && (
                        <p className="mt-1 text-xs text-slate-500">
                          на заказ: {formatMoney(preview.orderDiscount, currency)}
                        </p>
                      )}
                    </div>
                    <div className="rounded-2xl border border-[#3566f6]/20 bg-[#f5f8ff] p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#174bd7]">Итог к оплате</p>
                      <p className="mt-2 text-2xl font-semibold text-[#0b1538]">{formatMoney(preview.total, currency)}</p>
                    </div>
                  </div>
                  <div className="max-w-xs">
                    <Label>Скидка на заказ, %</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={form.discount_percent}
                      onChange={(e) => updateForm({ discount_percent: e.target.value })}
                      disabled={saving}
                      className="mt-2"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 bg-white shadow-sm">
                <CardHeader className="border-b border-slate-100 bg-slate-50/60">
                  <CardTitle className="text-lg">Быстрые подсказки</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 p-4 text-sm text-slate-600">
                  <p>• Общая скидка применяется к сумме после скидок по позициям.</p>
                  <p>• Чтобы удалить позицию, нажмите иконку корзины в строке и сохраните заказ.</p>
                  <p>• После сохранения заказ останется открытым в режиме редактирования.</p>
                </CardContent>
              </Card>
            </div>

            {isEdit && (
              <Card className="border-slate-200 bg-slate-50/60 shadow-sm">
                <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Статус заказа</p>
                    <p className="mt-1 text-sm text-slate-600">
                      {currentOrder?.order_number || "Заказ"} · {getCustomerOrderSourceLabel(form.source)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button type="button" variant="outline" onClick={close} disabled={saving || sendingToClient}>
                      Закрыть
                    </Button>
                    <Button type="submit" disabled={saving || sendingToClient}>
                      {saving && !sendingToClient && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Сохранить изменения
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleSendToClient}
                      disabled={saving || sendingToClient}
                    >
                      {sendingToClient ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="mr-2 h-4 w-4" />
                      )}
                      {sendingToClient ? "Сохраняем и отправляем..." : "Отправить клиенту"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {!isEdit && (
              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 pt-4">
                <Button type="button" variant="outline" onClick={close} disabled={saving}>
                  Отмена
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Создать заказ
                </Button>
              </div>
            )}

            {isEdit && (
              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 pt-4">
                <Button type="button" variant="outline" onClick={close} disabled={saving || sendingToClient}>
                  Отмена
                </Button>
                <Button type="submit" disabled={saving || sendingToClient}>
                  {saving && !sendingToClient && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Сохранить заказ
                </Button>
              </div>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
